import { useState } from 'react';
import { TreePine, Sparkles, Brain } from 'lucide-react';
import { motion } from 'framer-motion';
import ArbrePRIsMEModal from './ArbrePRIsMEModal';

interface ArbrePRIsMECardProps {
  index?: number;
}

// Leaders pour le Concept de l'Arbre PRIsME
const arbreLeaders = [
  { name: 'Jean-Pierre Leclerc', title: 'Directeur TI', avatar: '/JP.png' },
  { name: 'Eric Hains', title: 'Directeur TI', avatar: '/EH.png' },
  { name: 'Vincent Bourassa', title: 'Directeur TI', avatar: '/VB.png' },
  { name: 'Jean-Michel Caouette', title: 'Directeur TI', avatar: '/JMC.png' },
];

export default function ArbrePRIsMECard({ index = 0 }: ArbrePRIsMECardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.02, y: -5 }}
        onClick={() => setIsModalOpen(true)}
        className="relative isolate h-72 w-full overflow-hidden rounded-lg bg-emerald-900 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group border-2 border-emerald-500/30 hover:border-emerald-500"
      >
        {/* Background gradient */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Tree pattern overlay */}
        <div className="absolute inset-0 z-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 10 L50 40 M50 40 L30 60 M50 40 L70 60 M30 60 L20 80 M30 60 L40 80 M70 60 L60 80 M70 60 L80 80' stroke='white' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px',
        }} />

        {/* Badge AI - Disponible dans AI Assistant */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
          title="Disponible dans AI Assistant"
        >
          <Brain className="w-4 h-4 text-purple-200" />
          <span className="text-xs text-purple-100 font-semibold">AI</span>
        </motion.div>

        {/* Content */}
        <div className="relative z-[1] flex h-full flex-col justify-between p-6 text-white">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner group-hover:bg-white/30 transition-colors">
                <TreePine className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold drop-shadow">Le Concept de l'Arbre PRIsME</h3>
                <p className="text-xs text-white/80 drop-shadow">TRONC / BRANCHE / FEUILLES</p>
              </div>
            </div>
            
            <p className="text-sm text-white/90 leading-relaxed drop-shadow mb-4">
              Découvrez la <strong>métaphore fondamentale</strong> de PRIsME. Comprenez comment 
              la structure <strong>TRONC / BRANCHE / FEUILLES</strong> organise la connaissance, 
              les méthodologies et les livrables à plusieurs niveaux.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-white/80">
              <Sparkles className="w-4 h-4" />
              <span>Concept holistique</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Avatars des leaders */}
              <div className="flex -space-x-2">
                {arbreLeaders.map((leader, idx) => (
                  <div
                    key={idx}
                    className="w-7 h-7 rounded-full border-2 border-white/50 bg-white/20 backdrop-blur-sm overflow-hidden hover:scale-110 transition-transform cursor-pointer"
                    title={`${leader.name} - ${leader.title}`}
                  >
                    <img
                      src={leader.avatar}
                      alt={leader.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        if (target.parentElement) {
                          target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center text-white text-[10px] font-bold">${leader.name.split(' ').map(n => n[0]).join('')}</div>`;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="text-xs font-semibold text-white">
                Explorer →
              </div>
            </div>
          </div>
        </div>

        {/* Shine effect on hover */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </motion.button>

      <ArbrePRIsMEModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}

